import { getLanguage } from './i18n';
import qs from 'qs';
import config from '../config';

class Api {
  /**
   * retrieves metadata from specific indicator
   * @param  {Number}  indicatorId indicator identifier
   * @param  {Object}  [params={}] querystring parameters
   * @return {Promise}
   */
  static async getMetadata(indicatorId, params = {}) {
    // build metadata url
    const url = `${config.ENDPOINT_INDICATOR}/${indicatorId}/metadata`;
    const data = await this.getJson(url, params);

    return data.body.metadata;
  }

  /**
   * fetches area information
   * @param  {Number}  areaId      area identifier
   * @param  {Object}  [params={}] querystring parameters
   * @return {Promise}
   */
  static async getAreas(indicatorId, params = {}) {
    // build url
    const url = `${config.ENDPOINT_INDICATOR}/${indicatorId}/areas`;
    const data = await this.getJson(url, params);

    return data.body.areas;
  }

  /**
   * fetch dimensions data from specific indicator
   * @param  {Number}  indicatorId indicator identifier
   * @param  {Object}  [params={}] querystring parameters
   * @return {Promise}
   */
  static async getDimensions(indicatorId, params = {}) {
    // build dimensions url
    const url = `${config.ENDPOINT_INDICATOR}/${indicatorId}/dimensions`;
    const data = await this.getJson(url, params);

    return data.body.dimensions;
  }

  /**
   * fetches indicator data
   * @param  {Number}  indicatorId indicator identifier
   * @param  {Object}  [params={}] querystring parameters
   * @return {Promise}
   */
  static async getData(indicatorId, params = {}, isDowload = false) {
    // build dimensions url
    const url = `${config.ENDPOINT_INDICATOR}/${indicatorId}/data`;
    let response;

    if (isDowload) {
      this.download(url, params);
      return;
    }

    // conditionally use get or post
    if (params.members.split(',').length <= config.REQUEST_MAX_MEMBERS) {
      response = await this.get(url, params);
    } else {
      response = await this.post(url, params);
    }

    const dataJson = await response.json();
    if (!response.ok) throw new Error(dataJson.message);

    const {
      metadata: { indicator_id },
      data,
      metadata,
      sources,
      footnotes
    } = dataJson.body;

    return { indicator_id, data, metadata, sources, footnotes };
  }

  /**
   * fetches the indicators tree from server
   * @param  {Number}  [theme=27]  main theme
   * @param  {Object}  [params={}] querystring parameters
   * @return {Promise}
   */
  static async getThematicTree(theme = config.TREE_DEFAULT_THEME, params = {}) {
    // build tree url
    const url = `${config.ENDPOINT_THEMATIC_TREE}/${theme}`;
    const data = await this.getJson(url, params);

    return data.body;
  }

  /**
   *  Fetches JSON data from the specified URL using a GET request.
   *
   * @param {string} url - The URL of the endpoint
   * @param {object} params - An object containing optional parameters
   * @returns {Promise<any>}
   */
  static async getJson(url, params = {}) {
    const response = await this.get(url, params);
    const json = await response.json();

    if (!response.ok) throw new Error(json.message);

    return json;
  }

  /**
   * GET request
   * @param  {String}  url         URL to be appended to API URL
   * @param  {Object}  [params={}] querystring parameters
   */
  static async get(url, params = {}) {
    const response = await fetch(Api.buildUrl(url, params))
      .then(response => {
        if (response.status >= 400 && response.status < 600)
          throw new Error('Bad response from server');

        return response;
      })
      .catch(error => {
        throw new Error(error);
      });

    return response;
  }

  /**
   * This function performs a POST request to the specified URL.
   *
   * @param {string} url - The URL of the endpoint to send the request to.
   * @param {object} params - An object containing additional parameters for the request.
   */
  static async post(url, params = {}) {
    // extract members from params
    const { members, ...rest } = params;

    const formData = new FormData();
    formData.append('members', members);

    const response = await fetch(Api.buildUrl(url, rest), {
      method: 'POST',
      body: formData
    })
      .then(response => {
        if (response.status >= 400 && response.status < 600)
          throw new Error('Bad response from server');

        return response;
      })
      .catch(error => {
        throw new Error(error);
      });

    return response;
  }

  /**
   * download file request
   * @param  {String}  url         URL to be appended to API URL
   * @param  {Object}  [params={}] querystring parameters
   * @return {Promise}             file
   */
  static async download(url, params = {}) {
    let response;

    // conditionally use get or post
    if (params.members.length <= config.REQUEST_MAX_MEMBERS) {
      window.open(Api.buildUrl(url, params));
    } else {
      response = await this.post(url, params);

      if (response.ok) {
        const format = params.format === 'excel' ? 'xlsx' : params.format;
        const date = new Intl.DateTimeFormat('az').format(new Date());
        const blob = await response.blob();

        const anchorElement = document.createElement('a');
        const dataURL = URL.createObjectURL(blob);
        anchorElement.href = dataURL;
        anchorElement.download = 'data_' + date + '.' + format;
        anchorElement.click();

        URL.revokeObjectURL(dataURL);
      } else {
        console.error('Error downloading file:', response.statusText);
      }
    }
  }

  /**
   * creates URL string using parameters
   * @param  {String} url         URL to be appended to API URL
   * @param  {Object} [params={}] querystring parameters
   * @return {String}             URL
   */
  static buildUrl(url, params = {}) {
    params.app = 'databank';
    if (!params.lang) params.lang = getLanguage();

    return `${config.API_BASE_URL}/${url}?${qs.stringify(params)}`;
  }
}

export default Api;
